/*
 * Pictures in a reading frame (rendering plan, slice 3: docs/2026-10-17-bison-first-inbox-rendering-plan.md).
 * Pure, so every rule is testable without a browser.
 *
 * The server keeps every picture's real address to itself and hands the page a manifest of opaque
 * asset IDs, one per `<img>` of the original email in document order (`src/image-routes.ts`). This
 * module pairs the sanitized email's `<img>` tags with that manifest, decides what each tag shows, and
 * writes the frame's document so the frame itself never touches the network: a shown picture is a
 * `blob:` the page fetched through the proxy, everything else is a stand-in drawn here.
 *
 * Pairing is by order, and only when it is provable: the same number of inline pictures on both sides,
 * each pair of a compatible kind, and the same alternative text wherever both have one. Anything else
 * pairs nothing, and the message says its pictures cannot be shown here, rather than guessing which
 * picture is which. A content ID is never matched to an attachment here: the server's `cid_resolved`
 * entry is the only mapping trusted, so a mapped picture renders inline once and an unmapped one says so
 * where it stood.
 */
import { z } from "zod";
import { type EmailDisplay, prepareEmailDisplay } from "../../src/email-display.ts";
import {
  type EmailFrameLook,
  emailFrameDocument,
  emailFramePolicy,
  FRAME_TOKENS,
} from "../../src/email-frame.ts";

const ImageAssetSchema = z.strictObject({
  id: z.uuid(),
  position: z.number().int().nonnegative(),
  kind: z.enum(["external", "data", "cid_unresolved", "cid_resolved", "attachment"]),
  contentId: z.string().nullable(),
  attachmentId: z.string().nullable(),
  alt: z.string().nullable(),
});
export type ImageAsset = z.infer<typeof ImageAssetSchema>;
export const ImageManifestSchema = z.strictObject({ assets: z.array(ImageAssetSchema).max(512) });

/** What one asset is doing in this page. Transport detail never leaves `use-message-images.ts`. */
export type AssetState =
  | { readonly status: "loading" }
  | { readonly status: "shown"; readonly url: string }
  | { readonly status: "blocked" }
  | { readonly status: "busy" }
  | { readonly status: "unavailable" }
  | { readonly status: "unsupported" }
  | { readonly status: "failed" };

const IMAGE_TAG = /<img\b([^>]*?)(\s*\/?)>/g;
const NUMBER = /\sdata-lcr-image="(\d+)"/;

/*
 * The sanitizer quotes every attribute and text cannot contain `<`, so `<img` only ever opens a tag
 * (the same guarantee `email-frame.ts` relies on). Each tag is numbered once, before quoted history is
 * folded, so a number survives into whichever region the tag lands in.
 */
export function numberImages(html: string): string {
  let index = 0;
  return html.replaceAll(/<img\b/g, () => `<img data-lcr-image="${index++}"`);
}

/** Each message prepared for the frame: its pictures numbered, then its quoted history folded. */
export function prepareMailDisplay(
  html: string,
  earlier: readonly { id: string; html: string }[],
  parse: (html: string) => Document,
): EmailDisplay {
  return prepareEmailDisplay(numberImages(html), earlier, parse);
}

const attribute = (attributes: string, name: string): string | null =>
  new RegExp(`\\s${name}="([^"]*)"`).exec(attributes)?.[1] ?? null;

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', "#39": "'" };
const decode = (value: string): string =>
  value.replaceAll(/&(amp|lt|gt|quot|#39);/g, (_, name: string) => ENTITIES[name] ?? "");

type Tag = { index: number; source: "remote" | "data" | "none"; alt: string | null };

/** The numbered `<img>` tags of some display HTML, in document order. */
export function imageTags(html: string): Tag[] {
  return [...html.matchAll(IMAGE_TAG)].flatMap(([, attributes = ""]) => {
    const number = NUMBER.exec(attributes)?.[1];
    if (number === undefined) return [];
    const src = attribute(attributes, "src");
    const alt = attribute(attributes, "alt");
    return [
      {
        index: Number(number),
        source:
          attribute(attributes, "data-remote-src") !== null
            ? ("remote" as const)
            : src?.startsWith("data:")
              ? ("data" as const)
              : ("none" as const),
        alt: alt === null ? null : decode(alt),
      },
    ];
  });
}

const INLINE = new Set<ImageAsset["kind"]>(["external", "data", "cid_unresolved", "cid_resolved"]);

function compatible(tag: Tag, asset: ImageAsset): boolean {
  const kind =
    tag.source === "remote"
      ? asset.kind === "external"
      : tag.source === "data"
        ? asset.kind === "data"
        : asset.kind !== "data";
  return kind && (tag.alt === null || asset.alt === null || tag.alt === asset.alt);
}

/**
 * The manifest's inline pictures paired with the email's tags by order, or null when that pairing
 * cannot be proved. `html` is the whole display HTML, quoted history included.
 */
export function pairImages(
  html: string,
  assets: readonly ImageAsset[],
): ReadonlyMap<number, ImageAsset> | null {
  const tags = imageTags(html);
  const inline = assets
    .filter((asset) => INLINE.has(asset.kind))
    .sort((a, b) => a.position - b.position);
  if (tags.length !== inline.length) return null;
  const pairs = new Map<number, ImageAsset>();
  for (const [order, tag] of tags.entries()) {
    const asset = inline[order];
    if (!asset || tag.index !== order || !compatible(tag, asset)) return null;
    pairs.set(tag.index, asset);
  }
  return pairs;
}

/** What a tag in the frame should be when the document is written. */
export type TagPlan =
  /** Requested, or about to be: a stand-in of the picture's own size, swapped for it in place. */
  | { readonly kind: "pending"; readonly asset: ImageAsset }
  /** Embedded in the email by content ID with no proven attachment: a notice where it stood. */
  | { readonly kind: "missing" }
  /** From the sender, and this person hides those here: its alternative text where it stood. */
  | { readonly kind: "hidden" }
  /** Not requested: blocked, failed, unpaired or unknown. A quiet stand-in, no network. */
  | { readonly kind: "off" };

const LOADABLE = new Set<ImageAsset["kind"]>(["external", "data", "cid_resolved"]);

/** The plan for each numbered tag of the HTML the frame will show. */
export function planTags(
  html: string,
  pairs: ReadonlyMap<number, ImageAsset> | null,
  blocked: boolean,
): ReadonlyMap<number, TagPlan> {
  const plans = new Map<number, TagPlan>();
  for (const tag of imageTags(html)) {
    const asset = pairs?.get(tag.index);
    if (!asset) plans.set(tag.index, { kind: "off" });
    else if (asset.kind === "cid_unresolved") plans.set(tag.index, { kind: "missing" });
    else if (blocked && asset.kind === "external") plans.set(tag.index, { kind: "hidden" });
    else if (LOADABLE.has(asset.kind)) plans.set(tag.index, { kind: "pending", asset });
    else plans.set(tag.index, { kind: "off" });
  }
  return plans;
}

const ON_SCREEN = new Set<AssetState["status"] | undefined>([undefined, "loading", "shown"]);

/**
 * Whether the picture behind a numbered tag is on screen, or on its way, for the email's look
 * (`emailStyling`): a planned picture until it settles as anything but shown. Before the manifest is
 * in (`awaiting`), a tag with a source is taken as shown, as it will be unless it fails, and a tag
 * without one (an embedded or plain-http picture) as the stand-in it most often stays, so the look
 * changes at most once for the uncommon case.
 */
export function pictureShown(input: {
  html: string;
  plans: ReadonlyMap<number, TagPlan>;
  states: ReadonlyMap<string, AssetState>;
  awaiting: boolean;
  blocked: boolean;
}): (attributes: string) => boolean {
  const shown = new Set<number>();
  for (const tag of imageTags(input.html)) {
    const plan = input.plans.get(tag.index);
    const on =
      plan?.kind === "pending"
        ? ON_SCREEN.has(input.states.get(plan.asset.id)?.status)
        : input.awaiting && (tag.source === "data" || (tag.source === "remote" && !input.blocked));
    if (on) shown.add(tag.index);
  }
  return (attributes) => {
    const number = NUMBER.exec(attributes)?.[1];
    return number !== undefined && shown.has(Number(number));
  };
}

/** The assets a plan asks the proxy for, each once. */
export function wantedAssets(plans: ReadonlyMap<number, TagPlan>): ImageAsset[] {
  const seen = new Map<string, ImageAsset>();
  for (const plan of plans.values())
    if (plan.kind === "pending") seen.set(plan.asset.id, plan.asset);
  return [...seen.values()];
}

/** A transparent picture of the declared size: the stand-in keeps the picture's room while it loads. */
function sizedStandIn(attributes: string): string | null {
  const width = Number(attribute(attributes, "width") ?? 0);
  const height = Number(attribute(attributes, "height") ?? 0);
  if (!(width > 0 && height > 0 && width <= 4000 && height <= 4000)) return null;
  return `data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27${width}%27 height=%27${height}%27/%3E`;
}

/*
 * A picture that will not show says so where it stood, with its alternative text, so a message made
 * only of pictures still reads. `alt` comes from the sanitized markup, already escaped for both places.
 */
function imageNote(words: string, alt: string | null): string {
  const said = alt?.trim() ? `${words}: ${alt.trim()}` : words;
  return `<span class="lcr-image-note" role="img" aria-label="${said}">${said}</span>`;
}

const WITHOUT_SOURCE = /\s(?:src|data-remote-src)="[^"]*"/g;

function rewriteTag(attributes: string, close: string, plan: TagPlan | undefined): string {
  const kept = attributes.replaceAll(WITHOUT_SOURCE, "");
  if (plan?.kind === "missing" || plan?.kind === "hidden")
    return imageNote(
      plan.kind === "missing" ? "Embedded image unavailable" : "Image hidden",
      attribute(attributes, "alt"),
    );
  if (plan?.kind === "pending") {
    const standIn = sizedStandIn(attributes);
    const source = standIn ? ` src="${standIn}"` : "";
    return `<img${kept}${source} data-lcr-asset="${plan.asset.id}" data-lcr-loading=""${close}>`;
  }
  // `withImagePlaceholders` gives a tag with no source its quiet, line-high stand-in.
  return `<img${kept}${close}>`;
}

/** The display HTML with every picture planned: no tag keeps an address the frame could fetch. */
export function frameBody(html: string, plans: ReadonlyMap<number, TagPlan>): string {
  return html.replaceAll(IMAGE_TAG, (_tag, attributes: string, close: string) => {
    const number = NUMBER.exec(attributes)?.[1];
    return rewriteTag(
      attributes,
      close,
      number === undefined ? undefined : plans.get(Number(number)),
    );
  });
}

/*
 * Frame-side styles for pictures and their stand-ins. On the email's own light page they are the light
 * values of `paper`, `line-strong` and `muted`. Plain mail can hold a stand-in or a note too (a picture
 * not shown sets no page), so there they take the theme's tokens on the message card, as they do in
 * styled mail made dark.
 */
function pictureStyle(look: EmailFrameLook): string {
  const pick = look === "app-light" ? 0 : 1;
  const [stand, edge, ground, text] =
    look === "email"
      ? ["#ebe8e1", "#d3c7ab", "#f6f1e8", "#6e6860"]
      : [
          FRAME_TOKENS.line[pick],
          FRAME_TOKENS["line-strong"][pick],
          "transparent",
          FRAME_TOKENS.muted[pick],
        ];
  return (
    `img[data-lcr-loading]{background:${look === "email" ? "#efe9dc" : stand};border-radius:4px}` +
    "img[data-lcr-off]{width:auto!important;height:auto!important;max-width:min(100%,160px)!important;" +
    `max-height:40px!important;background:${stand};border-radius:4px;vertical-align:middle}` +
    ".lcr-image-note{display:inline-block;max-width:100%;box-sizing:border-box;margin:2px 0;" +
    `padding:3px 8px;border:1px dashed ${edge};border-radius:6px;background:${ground};color:${text};` +
    "font:12px/1.45 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,sans-serif;" +
    "overflow-wrap:anywhere;vertical-align:middle}"
  );
}

/**
 * The frame's document. Pictures may come only from inline stand-ins and the `blob:` addresses this
 * page made from the proxy's answers: the frame runs no script, so no other `blob:` can exist in it.
 * If the policy this extends ever changes shape, the stricter original is kept and nothing loads.
 */
export function readerFrameDocument(
  body: string,
  options: { quoted: boolean; look?: EmailFrameLook },
): string {
  const document = emailFrameDocument(body, { images: false, ...options });
  const policy = emailFramePolicy({ images: false });
  if (!policy.endsWith("img-src data:") || !document.includes(policy)) return document;
  return document
    .replace(policy, `${policy} blob:`)
    .replace("</head>", `<style>${pictureStyle(options.look ?? "email")}</style></head>`);
}

/** Any picture at all in this email, shown or folded. */
export const hasPictures = (html: string): boolean => /<img\b/.test(html);
